import React, { useState, useEffect } from 'react';
import { apiClient } from '../../utils/api';
import { toast } from 'sonner';
import { Loader2, Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const SettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [settings, setSettings] = useState({
    main_phone: '',
    secondary_phone: '',
    whatsapp_link: '',
    viber_link: '',
    email: '',
    address: '',
    seo_title: '',
    seo_description: '',
    facebook_url: '',
    instagram_url: '',
    linkedin_url: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const data = await apiClient.get('/api/settings/', { skipAuth: true });
      setSettings(data);
    } catch (error) {
      toast.error('Грешка при зареждане');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await apiClient.put('/api/settings/', settings);
      toast.success('Настройките са запазени');
    } catch (error) {
      toast.error(error.message || 'Грешка при запазване');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-serif font-semibold text-[#F6F2E8]">Настройки</h1>
        <p className="text-[#A9A9A9] mt-1">Глобални настройки на сайта</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Contact Information */}
        <div className="bg-[#111111] border border-[#D4AF37]/10 rounded-xl p-6 space-y-4">
          <h2 className="text-xl font-serif font-semibold text-[#F6F2E8] mb-4">Контактна информация</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Основен телефон *</Label>
              <Input
                required
                value={settings.main_phone}
                onChange={(e) => setSettings({...settings, main_phone: e.target.value})}
                className="bg-[#0B0B0B] border-[#D4AF37]/20"
                placeholder="+359 ..."
              />
            </div>

            <div>
              <Label>Втори телефон</Label>
              <Input
                value={settings.secondary_phone}
                onChange={(e) => setSettings({...settings, secondary_phone: e.target.value})}
                className="bg-[#0B0B0B] border-[#D4AF37]/20"
                placeholder="+359 ..."
              />
            </div>

            <div>
              <Label>Email *</Label>
              <Input
                required
                type="email"
                value={settings.email}
                onChange={(e) => setSettings({...settings, email: e.target.value})}
                className="bg-[#0B0B0B] border-[#D4AF37]/20"
                placeholder="info@deusestate.com"
              />
            </div>

            <div>
              <Label>Адрес *</Label>
              <Input
                required
                value={settings.address}
                onChange={(e) => setSettings({...settings, address: e.target.value})}
                className="bg-[#0B0B0B] border-[#D4AF37]/20"
                placeholder="гр. София, ..."
              />
            </div>

            <div>
              <Label>WhatsApp линк</Label>
              <Input
                value={settings.whatsapp_link}
                onChange={(e) => setSettings({...settings, whatsapp_link: e.target.value})}
                className="bg-[#0B0B0B] border-[#D4AF37]/20"
                placeholder="https://wa.me/359..."
              />
            </div>

            <div>
              <Label>Viber линк</Label>
              <Input
                value={settings.viber_link}
                onChange={(e) => setSettings({...settings, viber_link: e.target.value})}
                className="bg-[#0B0B0B] border-[#D4AF37]/20"
                placeholder="viber://chat?number=..."
              />
            </div>
          </div>
        </div>

        {/* SEO Settings */}
        <div className="bg-[#111111] border border-[#D4AF37]/10 rounded-xl p-6 space-y-4">
          <h2 className="text-xl font-serif font-semibold text-[#F6F2E8] mb-4">SEO настройки</h2>
          
          <div>
            <Label>SEO заглавие *</Label>
            <Input
              required
              value={settings.seo_title}
              onChange={(e) => setSettings({...settings, seo_title: e.target.value})}
              className="bg-[#0B0B0B] border-[#D4AF37]/20"
              placeholder="Deus Estate - Информираният избор при недвижими имоти"
            />
          </div>

          <div>
            <Label>SEO описание *</Label>
            <Input
              required
              value={settings.seo_description}
              onChange={(e) => setSettings({...settings, seo_description: e.target.value})}
              className="bg-[#0B0B0B] border-[#D4AF37]/20"
              placeholder="Професионални услуги за недвижими имоти..."
            />
          </div>
        </div>

        {/* Social Media */}
        <div className="bg-[#111111] border border-[#D4AF37]/10 rounded-xl p-6 space-y-4">
          <h2 className="text-xl font-serif font-semibold text-[#F6F2E8] mb-4">Социални мрежи</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Facebook</Label>
              <Input
                value={settings.facebook_url}
                onChange={(e) => setSettings({...settings, facebook_url: e.target.value})}
                className="bg-[#0B0B0B] border-[#D4AF37]/20"
                placeholder="https://facebook.com/..."
              />
            </div>

            <div>
              <Label>Instagram</Label>
              <Input
                value={settings.instagram_url}
                onChange={(e) => setSettings({...settings, instagram_url: e.target.value})}
                className="bg-[#0B0B0B] border-[#D4AF37]/20"
                placeholder="https://instagram.com/..."
              />
            </div>

            <div>
              <Label>LinkedIn</Label>
              <Input
                value={settings.linkedin_url}
                onChange={(e) => setSettings({...settings, linkedin_url: e.target.value})}
                className="bg-[#0B0B0B] border-[#D4AF37]/20"
                placeholder="https://linkedin.com/..."
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={saving}
            className="btn-primary"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Запазване...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Запази настройки
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SettingsPage;